import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useBuyerAuth } from '../../contexts/BuyerAuthContext';
import axios from 'axios';
import { ChevronLeft, ChevronRight, MapPin, X, Phone, CheckCircle, Car, Heart } from 'lucide-react';
import useSEO from '../../hooks/useSEO';
import { vehicleTitle } from '../../utils/vehicleTitle';
import { vehicleJsonLd } from '../../utils/vehicleJsonLd';
import { cityPageFor } from '../../utils/cityPage';
import { modelGuideFor } from '../../utils/modelGuide';
import { vehicleSpecRows, vehicleSummary, vehicleLocation } from '../../utils/vehicleFacts';
import { CITY_SEO } from './CityLanding';
import { cloudinaryUrl } from '../../utils/cloudinaryUrl';
import VehicleCard from '../../components/VehicleCard';
import ShowNumberButton from '../../components/ShowNumberButton';
import { addRecentlyViewed, isShortlisted, toggleShortlist } from '../../utils/buyerCollections';
import { vehicleSlug, vehiclePath, parseVehicleId } from '../../utils/vehicleSlug';
import { trackEvent } from '../../utils/analytics';
import './VehicleDetail.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const BASE_URL = 'https://www.buyselltaxi.com';

// A removed listing's page. The slug still names the car ("maruti-suzuki-
// dzire-2019-<uuid>"), so it can point at that model's guide or hub — the most
// useful next page for someone who followed an old link to a sold Dzire.
const ListingGone = ({ slug }) => {
  const guide = modelGuideFor({ model: String(slug || '').replace(/-/g, ' ') });
  return (
    <div className="listing-gone" style={{ minHeight: '100vh' }}>
      <h1>This listing is no longer available</h1>
      <p>The vehicle has been sold or taken down by the seller.</p>
      <ul>
        {guide && <li><Link to={guide.path}>{guide.label}</Link></li>}
        <li><Link to="/vehicles">Browse all used taxis for sale</Link></li>
        <li><Link to="/buy-taxi-delhi">Used taxis in Delhi</Link></li>
        <li><Link to="/buy-taxi-gurugram">Used taxis in Gurugram</Link></li>
      </ul>
    </div>
  );
};

const VehicleDetail = () => {
  const { slug } = useParams();
  // URLs are /vehicles/toyota-innova-crysta-2019-142; only the trailing id is
  // looked up. Legacy /vehicles/142 links parse to the same id and are
  // redirected to the slug form once the vehicle is known (see below).
  const id = parseVehicleId(slug);
  const navigate = useNavigate();
  const { buyer } = useBuyerAuth();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  // The API 404s a deleted listing (a sold one still loads, marked sold).
  const [notFound, setNotFound] = useState(false);
  const [currentImg, setCurrentImg] = useState(0);
  const [modal, setModal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [formSuccess, setFormSuccess] = useState(false);
  const [similar, setSimilar] = useState([]);
  const [saved, setSaved] = useState(false);
  
  // Unified form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    preferred_date: '',
    preferred_time: '',
    city: ''
  });

  useEffect(() => {
    if (!id) {
      navigate('/vehicles', { replace: true });
      return;
    }
    setLoading(true);
    setNotFound(false);
    setCurrentImg(0);
    axios.get(`${API_URL}/buyer/vehicles/${id}`)
      .then(res => setVehicle(res.data.vehicle))
      .catch(err => {
        // A removed listing used to bounce to /vehicles. Its URL returned 200
        // with the homepage's title and canonical, so Google kept dozens of
        // them as soft duplicates after stock fell from ~150 to 78. A 404
        // now renders a noindex "no longer available" page instead — Google's
        // documented approach for SPA not-found pages. Anything else (network,
        // 5xx) keeps the old redirect so a blip can never noindex a live car.
        if (err.response && err.response.status === 404) setNotFound(true);
        else navigate('/vehicles');
      })
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [id, navigate]);

  // Send every non-canonical spelling of this listing's URL to the canonical
  // one — the bare-id legacy form, a stale slug from before the listing was
  // edited, or anything a scraper invented with the right id on the end.
  // Keyed on id (not slug), so this replace does not re-trigger the fetch.
  useEffect(() => {
    if (!vehicle) return;
    const canonical = vehicleSlug(vehicle);
    if (canonical && slug !== canonical) {
      navigate(vehiclePath(vehicle), { replace: true });
    }
  }, [vehicle, slug, navigate]);

  // Track view + shortlist state, and load similar vehicles: nearest in price
  // within the same category. The route applies the same rule the build
  // injector uses for the static HTML (utils/similarVehicles.js).
  useEffect(() => {
    if (!vehicle) return;
    addRecentlyViewed(vehicle);
    setSaved(isShortlisted(vehicle.id));

    axios.get(`${API_URL}/buyer/vehicles/${vehicle.id}/similar`)
      .then(res => setSimilar((res.data.listings || []).filter(v => v.id !== vehicle.id).slice(0, 4)))
      .catch(() => setSimilar([]));
  }, [vehicle]);

  useEffect(() => {
    if (buyer) {
      setFormData(prev => ({
        ...prev,
        name: buyer.name || '',
        phone: buyer.phone || '',
        email: buyer.email || ''
      }));
    }
  }, [buyer]);

  // ── Dynamic SEO for vehicle listing ──────────────────────────────────────
  const vehicleSEO = useMemo(() => {
    if (!vehicle) return {};
    const price = Number(vehicle.price_final).toLocaleString('en-IN');
    const km = vehicle.km_run ? `${Number(vehicle.km_run).toLocaleString('en-IN')} km` : '';
    const loc = [vehicle.city, vehicle.state].filter(Boolean).join(', ');
    const cityStr = vehicle.city || '';
    // Title formula lives in src/utils/vehicleTitle.js, shared with the build
    // injector so the crawler and the visitor read the same thing. The odometer
    // is deliberately kept — it is what separates near-identical listings.
    const title = vehicleTitle(vehicle);
    const rawDesc = `${vehicle.title} for sale at ₹${price}. ${km ? km + ' driven. ' : ''}${vehicle.fuel_type || ''} ${vehicle.transmission || ''}. Located in ${loc}. ${vehicle.badge_type === 'bst_listing' ? 'BST Verified.' : 'Direct seller.'} Contact now.`;
    const description = rawDesc.length > 155 ? rawDesc.substring(0, 152) + '...' : rawDesc;
    const keywords = [
      `${vehicle.brand} ${vehicle.model} for sale`,
      `buy ${vehicle.brand} ${vehicle.model}`,
      `${vehicle.year} ${vehicle.brand} ${vehicle.model}`,
      vehicle.city ? `taxi for sale ${vehicle.city}` : null,
      vehicle.city ? `commercial vehicle ${vehicle.city}` : null,
      `${(vehicle.category || '').toLowerCase()} for sale India`,
      'buy sell taxi', 'commercial vehicle marketplace India'
    ].filter(Boolean).join(', ');
    const image = vehicle.images?.[0] || `${BASE_URL}/og-image.jpg`;
    const url = `${BASE_URL}${vehiclePath(vehicle)}`;

    const jsonLd = vehicleJsonLd(vehicle, {
      url, description, image, baseUrl: BASE_URL,
      cityPage: cityPageFor(vehicle.city, CITY_SEO),
    });

    return { title, description, keywords, image, url, jsonLd };
  }, [vehicle]);

  useSEO(notFound
    // Self canonical: without a url useSEO falls back to the homepage, which is
    // exactly the wrong signal the old redirect was sending.
    ? { title: 'Listing No Longer Available', noindex: true, follow: true, url: `${BASE_URL}/vehicles/${slug}` }
    : {
        title: vehicleSEO.title,
        description: vehicleSEO.description,
        keywords: vehicleSEO.keywords,
        image: vehicleSEO.image,
        url: vehicleSEO.url,
        type: 'product',
        jsonLd: vehicleSEO.jsonLd,
      });

  const showToast = (text, type = 'info') => { 
    setToast({ text, type }); 
    setTimeout(() => setToast(null), 5000); 
  };

  const handleCTA = (type) => {
    setModal(type);
    setFormSuccess(false);
    // Reset form except pre-filled buyer info
    setFormData(prev => ({
      ...prev,
      message: '',
      preferred_date: '',
      preferred_time: '',
      city: ''
    }));
  };

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Submit callback/interest request
  const submitCallbackRequest = async () => {
    if (!formData.name || !formData.phone) {
      showToast('Please enter your name and phone number', 'error');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/buyer/callback-request`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        request_type: 'vehicle_interest',
        vehicle_id: id,
        vehicle_title: vehicle?.title,
        message: formData.message || `Interested in: ${vehicle?.title}`
      });

      setFormSuccess(true);
      showToast(res.data.message, 'success');
      trackEvent('enquiry_submit', {
        enquiry_type: modal === 'callback' ? 'callback' : 'interest',
        vehicle_id: id,
        vehicle_title: vehicle?.title,
      });
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to submit request', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit test drive request
  const submitTestDriveRequest = async () => {
    if (!formData.name || !formData.phone) {
      showToast('Please enter your name and phone number', 'error');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/buyer/test-drive-request`, {
        buyer_name: formData.name,
        buyer_phone: formData.phone,
        buyer_email: formData.email,
        vehicle_id: id,
        vehicle_title: vehicle?.title,
        preferred_date: formData.preferred_date,
        preferred_time: formData.preferred_time,
        city: formData.city
      });

      setFormSuccess(true);
      showToast(res.data.message, 'success');
      trackEvent('enquiry_submit', {
        enquiry_type: 'test_drive',
        vehicle_id: id,
        vehicle_title: vehicle?.title,
      });
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to submit request', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Hold a full screen while loading. React replaces the server-rendered body
  // on mount, and a short loading line let the footer jump up into view and
  // back down when the data landed — a 0.687 layout shift on every listing
  // (Lighthouse, 2026-09-26). Off-screen movement does not count as a shift.
  if (loading) return <div className="loading" style={{ minHeight: '100vh' }}>Loading vehicle details...</div>;
  if (notFound) return <ListingGone slug={slug} />;
  if (!vehicle) return <div className="loading" style={{ minHeight: '100vh' }}>Vehicle not found</div>;

  const images = vehicle.images || [];
  const prevImg = () => setCurrentImg(i => (i - 1 + images.length) % images.length);
  const nextImg = () => setCurrentImg(i => (i + 1) % images.length);

  // Specs, location and summary come from utils/vehicleFacts.js, which the build
  // injector also uses — the static body an AI crawler reads is this page's copy.
  const specs = vehicleSpecRows(vehicle);

  // Listing → its city landing page (indexable ones only). Same rule the build
  // injector uses for the crawler-facing body — see utils/cityPage.js.
  const cityPage = cityPageFor(vehicle.city, CITY_SEO);
  // Listing → its model's buying guide or hub, same rule as the build injector.
  const modelGuide = modelGuideFor(vehicle);
  const location = vehicleLocation(vehicle);

  const isReserved = vehicle.status === 'reserved';
  const isSold = vehicle.status === 'sold';

  // Per-vehicle WhatsApp enquiry — prefilled so the team knows which car it's about
  const waMessage = encodeURIComponent(
    `Hi, I'm interested in ${vehicle.year} ${vehicle.brand} ${vehicle.model} ` +
    `(₹${Number(vehicle.price_final).toLocaleString('en-IN')}${vehicle.city ? ', ' + vehicle.city : ''}) — ` +
    `${BASE_URL}${vehiclePath(vehicle)}`
  );
  const waLink = `https://wa.me/919560759834?text=${waMessage}`;
  const trackWhatsApp = (placement) => trackEvent('whatsapp_enquiry_click', {
    placement,
    vehicle_id: vehicle.id,
    vehicle_title: vehicle.title,
  });

  const onToggleSave = () => {
    const nowSaved = toggleShortlist(vehicle);
    setSaved(nowSaved);
    showToast(nowSaved ? 'Added to your shortlist' : 'Removed from your shortlist', 'success');
  };

  // Get tomorrow's date for min date picker
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  return (
    <div className="vehicle-detail" data-testid="vehicle-detail-page">
      {toast && (
        <div className={`toast-msg toast-${toast.type}`} data-testid="toast-message">
          {toast.text}
        </div>
      )}

      <div className="detail-layout">
        {/* Gallery */}
        <div className="gallery" data-testid="photo-gallery">
          <div className="main-image">
            {images.length > 0 ? (
              <>
                <img
                  src={cloudinaryUrl(images[currentImg], 1200)}
                  alt={`${vehicle.year} ${vehicle.brand} ${vehicle.model} used taxi for sale - ${vehicle.city || 'India'}`}
                  /* This is the LCP element on a listing page — never lazy, and
                     worth jumping the queue for. .main-image carries a 4/3
                     aspect-ratio, so it reserves its box and cannot shift. */
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                />
                {images.length > 1 && (
                  <>
                    <button className="nav-arrow left" onClick={prevImg}><ChevronLeft size={28} /></button>
                    <button className="nav-arrow right" onClick={nextImg}><ChevronRight size={28} /></button>
                  </>
                )}
                <div className="img-counter">{currentImg + 1} / {images.length}</div>
              </>
            ) : (
              <div className="no-image">No photos available</div>
            )}
            {(isReserved || isSold) && (
              <div className={`status-overlay ${vehicle.status}`}>
                {isReserved ? 'RESERVED' : 'SOLD'}
              </div>
            )}
          </div>
          {images.length > 1 && (
            <div className="thumb-strip">
              {images.map((img, i) => (
                <img key={i} src={cloudinaryUrl(img, 200)}
                  alt={`${vehicle.year} ${vehicle.brand} ${vehicle.model} - photo ${i + 1}`}
                  className={`thumb ${i === currentImg ? 'active' : ''}`}
                  loading="lazy" decoding="async"
                  onClick={() => setCurrentImg(i)} data-testid={`thumb-${i}`} />
              ))}
            </div>
          )}
        </div>

        {/* Info Panel */}
        <div className="info-panel">
          <h1 data-testid="vehicle-title">
            {vehicle.year} {vehicle.brand} {vehicle.model}
            {vehicle.city ? ` – Used Taxi for Sale in ${vehicle.city}` : ' – Used Taxi for Sale'}
          </h1>
          <div className="price-row">
            <span className="price" data-testid="vehicle-price">₹{Number(vehicle.price_final).toLocaleString('en-IN')}</span>
            <span className={`badge-pill badge-${vehicle.badge_type}`} data-testid="vehicle-badge">
              {vehicle.badge_type === 'bst_listing' ? 'BST LISTING' : 'DIRECT SELLER'}
            </span>
            {vehicle.is_sponsored && <span className="sponsored-tag-sm">Sponsored</span>}
            <button
              className={`save-btn ${saved ? 'saved' : ''}`}
              onClick={onToggleSave}
              aria-pressed={saved}
              data-testid="save-vehicle"
            >
              <Heart size={16} /> {saved ? 'Saved' : 'Save'}
            </button>
          </div>
          <p className="vehicle-summary" data-testid="vehicle-summary">{vehicleSummary(vehicle)}</p>
          <div className="location-row" data-testid="vehicle-location">
            <MapPin size={16} /> {location}
            {cityPage && (
              <Link to={cityPage.path} className="location-city-link">
                More used taxis in {cityPage.name} →
              </Link>
            )}
          </div>

          {/* CTA Buttons */}
          <div className="cta-buttons" data-testid="cta-buttons">
            {!isReserved && !isSold ? (
              <>
                <button className="cta-primary" onClick={() => handleCTA('interest')} data-testid="cta-interested">
                  I'm Interested
                </button>
                {/* Inside this branch on purpose: a reserved or sold car has
                    nothing to reveal, and the server 409s on one anyway. */}
                <ShowNumberButton vehicle={vehicle} />
                <button className="cta-test-drive" onClick={() => handleCTA('test_drive')} data-testid="cta-test-drive">
                  <Car size={18} />
                  Book a Test Drive
                </button>
                <button className="cta-callback" onClick={() => handleCTA('callback')} data-testid="cta-callback">
                  <Phone size={18} />
                  Request Callback
                </button>
                <a href={waLink} onClick={() => trackWhatsApp('cta_button')} target="_blank" rel="noopener noreferrer" className="cta-whatsapp" data-testid="cta-whatsapp">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  WhatsApp Enquiry
                </a>
              </>
            ) : (
              <div className="vehicle-unavailable">
                <p>This vehicle is {isReserved ? 'currently reserved' : 'sold'}.</p>
                <button className="cta-secondary" onClick={() => navigate('/vehicles')}>
                  Browse Other Vehicles
                </button>
              </div>
            )}
          </div>

          {/* Contact Info Banner */}
          <div className="contact-banner" data-testid="contact-banner">
            <p>For priority listing or instant booking:</p>
            <a href="tel:+919560759834" className="contact-phone">
              <Phone size={16} /> +91 95607 59834
            </a>
            <a href={waLink} onClick={() => trackWhatsApp('contact_banner')} target="_blank" rel="noopener noreferrer" className="contact-whatsapp">
              WhatsApp Us
            </a>
          </div>

          {/* Specs Table */}
          <div className="specs-table" data-testid="specs-table">
            <h3>Specifications</h3>
            <div className="specs-grid">
              {specs.map(([label, value]) => (
                <div key={label} className="spec-item">
                  <span className="spec-label">{label}</span>
                  <span className="spec-value">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {vehicle.description && (
            <div className="description" data-testid="vehicle-description">
              <h3>Description</h3>
              <p>{vehicle.description}</p>
            </div>
          )}

          {modelGuide && (
            <p className="model-guide-link">
              Before you buy: <Link to={modelGuide.path}>{modelGuide.label}</Link>
              {' '}— prices, running costs and what to check.
            </p>
          )}
        </div>
      </div>

      {/* SIMILAR VEHICLES */}
      {similar.length > 0 && (
        <section className="similar-section" data-testid="similar-vehicles">
          <h2>Similar Vehicles</h2>
          <div className="similar-grid">
            {similar.map(v => (
              <VehicleCard key={v.id} v={v} testId={`similar-card-${v.id}`} source="similar" />
            ))}
          </div>
        </section>
      )}

      {/* MODALS */}
      {modal && (
        <div className="modal-overlay" onClick={() => !submitting && setModal(null)}>
          <div className="enquiry-modal" onClick={(e) => e.stopPropagation()} data-testid="enquiry-modal">
            <button className="close-btn" onClick={() => !submitting && setModal(null)} disabled={submitting}>
              <X size={24} />
            </button>

            {/* Interest / Callback Modal */}
            {(modal === 'interest' || modal === 'callback') && !formSuccess && (
              <>
                <h2>{modal === 'interest' ? "I'm Interested" : 'Request Callback'}</h2>
                <p className="modal-vehicle">{vehicle.title}</p>
                
                <div className="form-group">
                  <label>Your Name *</label>
                  <input 
                    type="text" 
                    value={formData.name} 
                    onChange={(e) => updateForm('name', e.target.value)}
                    placeholder="Enter your name" 
                    data-testid="form-name" 
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input 
                    type="tel" 
                    value={formData.phone} 
                    onChange={(e) => updateForm('phone', e.target.value)}
                    placeholder="Enter your phone number" 
                    data-testid="form-phone" 
                  />
                </div>
                <div className="form-group">
                  <label>Email (optional)</label>
                  <input 
                    type="email" 
                    value={formData.email} 
                    onChange={(e) => updateForm('email', e.target.value)}
                    placeholder="Enter your email" 
                    data-testid="form-email" 
                  />
                </div>
                <div className="form-group">
                  <label>Message (optional)</label>
                  <textarea 
                    value={formData.message} 
                    onChange={(e) => updateForm('message', e.target.value)}
                    placeholder="Any specific questions..."
                    rows={3}
                    data-testid="form-message" 
                  />
                </div>
                
                <button 
                  className="cta-primary" 
                  onClick={submitCallbackRequest} 
                  disabled={submitting || !formData.name || !formData.phone}
                  data-testid="submit-form"
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
                
                <p className="modal-note">
                  Or call us directly: <a href="tel:+919560759834">+91 95607 59834</a>
                </p>
              </>
            )}

            {/* Test Drive Modal */}
            {modal === 'test_drive' && !formSuccess && (
              <>
                <h2>Book a Test Drive</h2>
                <p className="modal-vehicle">{vehicle.title}</p>
                <p className="modal-subtitle">Fill in your details and we'll confirm your test drive within 24 hours.</p>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Your Name *</label>
                    <input 
                      type="text" 
                      value={formData.name} 
                      onChange={(e) => updateForm('name', e.target.value)}
                      placeholder="Enter your name" 
                      data-testid="td-name" 
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input 
                      type="tel" 
                      value={formData.phone} 
                      onChange={(e) => updateForm('phone', e.target.value)}
                      placeholder="Enter your phone" 
                      data-testid="td-phone" 
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Email (optional)</label>
                  <input 
                    type="email" 
                    value={formData.email} 
                    onChange={(e) => updateForm('email', e.target.value)}
                    placeholder="Enter your email" 
                    data-testid="td-email" 
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Preferred Date</label>
                    <input 
                      type="date" 
                      value={formData.preferred_date} 
                      onChange={(e) => updateForm('preferred_date', e.target.value)}
                      min={getTomorrowDate()}
                      data-testid="td-date" 
                    />
                  </div>
                  <div className="form-group">
                    <label>Preferred Time</label>
                    <select 
                      value={formData.preferred_time} 
                      onChange={(e) => updateForm('preferred_time', e.target.value)}
                      data-testid="td-time"
                    >
                      <option value="">Select time</option>
                      <option value="Morning (9AM-12PM)">Morning (9AM-12PM)</option>
                      <option value="Afternoon (12PM-4PM)">Afternoon (12PM-4PM)</option>
                      <option value="Evening (4PM-7PM)">Evening (4PM-7PM)</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>City</label>
                  <input 
                    type="text" 
                    value={formData.city} 
                    onChange={(e) => updateForm('city', e.target.value)}
                    placeholder="Your city for test drive" 
                    data-testid="td-city" 
                  />
                </div>
                
                <button 
                  className="cta-primary cta-test-drive-submit" 
                  onClick={submitTestDriveRequest} 
                  disabled={submitting || !formData.name || !formData.phone}
                  data-testid="submit-test-drive"
                >
                  {submitting ? 'Submitting...' : 'Book Test Drive'}
                </button>
                
                <p className="modal-note">
                  Our team will confirm your schedule within 24 hours.
                </p>
              </>
            )}

            {/* Success State */}
            {formSuccess && (
              <div className="form-success" data-testid="form-success">
                <CheckCircle size={60} className="success-icon" />
                <h2>Thank You!</h2>
                {modal === 'test_drive' ? (
                  <>
                    <p>Your test drive request has been submitted!</p>
                    <p className="success-subtitle">Our team will confirm your schedule within 24 hours.</p>
                  </>
                ) : (
                  <p>Our team will contact you shortly.</p>
                )}
                <p className="success-vehicle">Vehicle: {vehicle.title}</p>
                <button className="cta-primary" onClick={() => setModal(null)}>
                  Continue Browsing
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleDetail;
